import { AgTechFormContext } from '@agtech/core/forms/AgTechFormContext';
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from '@agtech/web/components/Buttons/AgTechButtons';
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import { AgTechGridNumberControl } from '@agtech/web/components/Grids/Controls/AgTechGridNumberControl';
import { LotSplit, LotSplitSummary, SaleLot } from '@sales/data/lots/LotData';
import { useLotDataSplitLotAction } from '@sales/data/lots/LotDataActions';
import React from 'react'
import { SaleContactGridLink } from '../../contacts/components/buttons/SaleContactButtons';
import { AgTechQueryBuilder, useAgTechQuery, useAgTechQueryResultCombination } from '@agtech/core/data/queries/AgTechDataQueries';
import { LotDataGetLotSplitSummaryQuery } from '@sales/data/lots/LotDataQueries';
import { useBidderDataGetSaleBidderDataQuery } from '@sales/data/bidders/BidderDataQueries';
import { AgTechIndex } from '@agtech/core/utilities/AgTechUtilities';
import { SaleBidder } from '@sales/data/bidders/BidderData';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { useContactSelectionProps } from '@agtech/web/integrations/contacts/components/selections/ContactSelectionControl';
import { AgTechContact, getContactFullName } from '@agtech/data/contacts/ContactData';
import { ContactSelectionButton } from '@agtech/web/integrations/contacts/components/buttons/ContactSelectionButton';
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { LotSplitDialogTourConfig } from './LotSplitDialogTourConfig';

export type LotSplitDialogProps = {
    lot: SaleLot
}

const LotSplitDialog = (props: LotSplitDialogProps) => {
    const lotSplitAction = useLotDataSplitLotAction();
    const lotSplitDialogQuery = useLotSplitDialogQuery(props);
    
    return (
        <AgTechFormDialog
            width={550}
            structure={() => ({
                header: {
                    header: 'Split Lot',
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={LotSplitDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: 'Split Lot'
            })}
            dialogData={{
                query: lotSplitDialogQuery,
                getDialogData: d => d
            }}
            dialogAction={{
                action: lotSplitAction,
                getActionData: d => ({
                    lotId: d.lotSplitSummary.lot.id,
                    lotTimestamp: d.lotSplitSummary.lot.timestamp,
                    bidderPercentages: d.lotSplitSummary.splits
                })
            }}
        >
            {(formContext) => (
                <LotSplitGrid 
                    lot={props.lot}
                    formContext={formContext}
                />
            )}
        </AgTechFormDialog>
    )
}

type LotSplitDialogData = {
    lot: SaleLot,
    lotSplitSummary: LotSplitSummary,
    bidders: AgTechIndex<SaleBidder>,
    contacts: AgTechIndex<AgTechContact>
}

export type InvoiceLotSplitGroupingGridProps = {
    lot: SaleLot,
    formContext: AgTechFormContext<LotSplitDialogData>,
    updateLotSplits?: (updatedLotSplits: LotSplit[]) => void,
}

const LotSplitGrid = (props: InvoiceLotSplitGroupingGridProps) => {
    const dialogData = props.formContext.entity;
    const lot = props.lot;

    const contactSelectionProps = useContactSelectionProps({
        onContactSelected: async (sel) => {},
        selectedContactId: undefined
    });

    return (
        <div id='lot-split-grid' className='stretched row-based container'>
        <AgTechGrid
            data={{
                itemQuery: AgTechQueryBuilder.buildQueryResult({
                    data: {
                        items: dialogData.lotSplitSummary.splits.map(item => ({
                            id: item.contactId,
                            bidder: item.bidderId ? dialogData.bidders[item.bidderId] : undefined,
                            contact: dialogData.contacts[item.contactId],
                            percentage: item.percentage
                        }))
                    }
                })
            }}
            header={{
                title: 'Lot Splits: ' + lot.lotNumber,
                showCount: false,
                content: () => (
                    <div id='lot-split-add-contact' className='ml-auto'>
                    <ContactSelectionButton
                        filter={cnt => !dialogData.lotSplitSummary.splits.find(s => s.contactId === cnt.id)}
                        onContactSelected={async selectedContact => {
                            props.formContext.updateFormEntity(curr => ({
                                ...curr,
                                lotSplitSummary: {
                                    ...curr.lotSplitSummary,
                                    splits: [...curr.lotSplitSummary.splits, {
                                        contactId: selectedContact.id,
                                        percentage: 0
                                    }]
                                }
                            }));
                        }}
                        classes='link'
                    />
                    </div>
                )
            }}
            columns={[
                {
                    header: 'Bidder Number',
                    value: i => i.bidder?.bidderNumber ?? 'N/A',
                    classes: 'centered bg-slate-100 emphasized',
                    width: '120px',
                },
                {
                    header: 'Name',
                    value: i => i.contact ? <SaleContactGridLink contactId={i.contact.id} contactName={getContactFullName(i.contact)} /> : 'Not Registered',
                },
                {
                    header: 'Percentage',
                    value: i => false ? (i.percentage * 100) : (
                        <div className='column-based-vertically-centered relative stretched container'>
                            <AgTechGridNumberControl
                                value={i.percentage * 100}
                                onUpdated={val => {
                                    props.formContext.updateFormEntity(curr => ({
                                        ...curr,
                                        lotSplitSummary: {
                                            ...curr.lotSplitSummary,
                                            splits: curr.lotSplitSummary.splits.map(s => s.contactId === i.id
                                                ? { ...s, percentage: val / 100 }
                                                : { ...s }
                                            )
                                        }
                                    }));
                                }}
                            />
                            <i className='bi bi-percent absolute' style={{ left: 60, marginTop: 1 }} />
                        </div>
                    ),
                    width: '120px',
                }
            ]}
            isRowEntityTheSame={() => false}
        />
        </div>
    )
}

const useLotSplitDialogQuery = (props: LotSplitDialogProps) => {
    const lotSplitQuery = useAgTechQuery({
        query: LotDataGetLotSplitSummaryQuery,
        queryContext: props.lot,
        projection: (lotSplitSummary) => ({
            lot: props.lot,
            lotSplitSummary: lotSplitSummary
        })
    });

    const bidderDataQuery = useBidderDataGetSaleBidderDataQuery();

    return useAgTechQueryResultCombination({
        firstQuery: lotSplitQuery,
        secondQuery: bidderDataQuery,
        projection: (lotSplitData, bidderData) => {
            return {
                ...lotSplitData,
                bidders: bidderData.bidders,
                contacts: bidderData.contacts
            }
        }
    })
}

LotSplitDialog.Open = (e: AgTechClickEventHandler, props: LotSplitDialogProps) => e.dialogs.openDialog({
    dialog: () => <LotSplitDialog {...props} />
});

LotSplitDialog.Button = (props: LotSplitDialogProps & AgTechButtonProps) => {
    return (
        <AgTechButton
            text="Split Lot"
            icon="fas fa-clone"
            {...props}
            action={e => LotSplitDialog.Open(e, props)}
        />
    )
}

export default LotSplitDialog